import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { MAX_CONTENT_WIDTH, ROUTE_CREATE_ORDER, ROUTE_CUSTOMER_ORDERS } from 'appConstants';
import { Wizard } from 'components';
import { useAddresses } from 'contexts/Addresses';
import { useOrders } from 'contexts/Orders';
import { useProducts } from 'contexts/Products';
import { useNotification } from 'shared/Notification';

import CustomerDetails from './CustomerDetails';
import useCustomerSchema from './CustomerDetails/useValidationSchema';
import OrderProducts from './OrderProducts';
import useProductsValidationSchema from './OrderProducts/useValidationSchema';
import OrderSummary from './OrderSummary';
import OrderWizardProvider from './OrderWizardProvider';
import type { IOrderWizard } from './types';

const defaultStepProps = {
  containerGridProps: { maxWidth: MAX_CONTENT_WIDTH },
  actionsGridProps: { width: MAX_CONTENT_WIDTH + 16 },
};

export default function OrderWizard({
  routePath,
  onCancel,
  initialFormValues,
  setTouchedOnMount,
}: IOrderWizard) {
  const customerSchema = useCustomerSchema();
  const productsSchema = useProductsValidationSchema();
  const {
    state: { orderSaving },
    actions: { saveOrder },
  } = useOrders();
  const {
    actions: { resetProducts },
  } = useProducts();

  const navigate = useNavigate();
  const { t } = useTranslation('CreateOrder');
  const { notification } = useNotification();

  const redirectToOrder = useCallback(
    (customerId) => {
      navigate(`${ROUTE_CUSTOMER_ORDERS}/${customerId}`);
    },
    [navigate],
  );

  const handleSubmit = useCallback(
    (values) => {
      const { customer: { customerReference = '' } = {} } = values;
      saveOrder(
        values,
        () => {
          resetProducts();
          redirectToOrder(customerReference);
        },
        () => {
          notification(t('errors.unableToCreateOrder'), 'error');
        },
      );
    },
    [saveOrder, redirectToOrder, notification, t, resetProducts],
  );

  const { internalOrderId = '' } = initialFormValues;

  const {
    actions: { resetAddresses },
  } = useAddresses();

  useEffect(
    () => () => {
      resetAddresses();
    },
    [resetAddresses],
  );

  return (
    <OrderWizardProvider>
      <Wizard
        key={internalOrderId}
        steps={[
          {
            ...defaultStepProps,
            component: <CustomerDetails />,
            path: '/',
            validationSchema: customerSchema,
          },
          {
            ...defaultStepProps,
            component: <OrderProducts />,
            path: '/products',
            validationSchema: productsSchema,
          },
          {
            ...defaultStepProps,
            component: <OrderSummary />,
            path: '/summary',
            containerGridProps: { lg: 10, sm: 12, maxWidth: 849 },
          },
        ]}
        isSaving={orderSaving}
        initialFormValues={initialFormValues}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        routePath={routePath}
        submitLabel={t('buttons.submitOrder')}
        setTouchedOnMount={setTouchedOnMount}
        promptData={{
          title: t('dialogs.unsavedChanges.title'),
          message: t('dialogs.unsavedChanges.description'),
          onActionLabel: t('dialogs.unsavedChanges.buttonPrimary'),
          baseExcludedPath: ROUTE_CREATE_ORDER,
        }}
      />
    </OrderWizardProvider>
  );
}
